<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import clsx from 'clsx'
import { onClickOutside, rand } from '@vueuse/core'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontSize } from '@tiptap/extension-text-style/font-size'
import Underline from '@tiptap/extension-underline'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { useAiStore } from '@/stores/modal/ai'
import { useMediaManagerStore } from '@/stores/modal/media-manager'
import { useModalPresetStore } from '@/stores/modal/presets'

const props = defineProps<{
    element: any
    extra: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    sectionHasBackgroundImage?: boolean
    isFull: boolean
    isNew?: boolean
    isNewBefore?: boolean
    isChild?: boolean
    elementParent?: any
    elementParentIndex?: number
    isEditable?: boolean
    isDarkMode: boolean
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()
const aiStore = useAiStore()
const mediaManagerStore = useMediaManagerStore()
const presetsStore = useModalPresetStore()

const editorText = ref<HTMLElement | null>(null)
const content = computed(() => props.element.frameContent)
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const isEditing = computed(() => betaStore.isEditingElement(elementId.value))

const getActiveTextSelectionState = (currentEditor: any) => {
    if (!currentEditor) {
        return {
            bold: false,
            italic: false,
            underline: false,
            strike: false,
            bulletList: false,
            orderedList: false,
            textAlign: null,
            fontSize: null,
        }
    }

    const textAlign = (['left', 'center', 'right', 'justify'] as const).find((alignment) => (
        currentEditor.isActive({ textAlign: alignment })
    )) ?? null

    return {
        bold: currentEditor.isActive('bold'),
        italic: currentEditor.isActive('italic'),
        underline: currentEditor.isActive('underline'),
        strike: currentEditor.isActive('strike'),
        bulletList: currentEditor.isActive('bulletList'),
        orderedList: currentEditor.isActive('orderedList'),
        textAlign,
        fontSize: currentEditor.getAttributes('textStyle')?.fontSize ?? null,
    }
}

const syncActiveTextSelectionState = (currentEditor: any) => {
    betaStore.setActiveTextSelectionState(getActiveTextSelectionState(currentEditor))
    betaStore.setActiveTextSelectionRange(currentEditor?.state?.selection)
}

const editor = useEditor({
    content: content.value,
    extensions: [
        StarterKit.configure({
            link: false,
            underline: false,
        }),
        Underline,
        TextStyle,
        Color,
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                rel: null,
            },
        }),
        Placeholder.configure({ placeholder: 'Adicione seu texto aqui...' }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Highlight.configure({ multicolor: true }),
        FontSize,
    ],
    onFocus() {
        betaStore.setSelectedElementBySchema(props.schema, props.element)
        betaStore.setEditingElement(props.element)
        betaStore.setActiveTextEditor(editor.value)
        syncActiveTextSelectionState(editor.value)
    },
    onSelectionUpdate() {
        betaStore.setSelectedElementBySchema(props.schema, props.element)
        betaStore.setEditingElement(props.element)
        betaStore.setActiveTextEditor(editor.value)
        syncActiveTextSelectionState(editor.value)
    },
    onTransaction({ editor }) {
        if (betaStore.activeTextEditor === editor) {
            syncActiveTextSelectionState(editor)
        }
    },
    onUpdate({ editor }) {
        betaStore.patchElementBySchema(props.schema, {
            frameContent: editor.getHTML(),
            new: false,
        })
        syncTextContainerHeight()
    },
})

const hasExtra = computed(() => props.extra != null)
const isCustomBackgroundColor = computed(() => props.extra?.background_color && props.extra?.background_color != '')
const isCustomParentBackgroundColor = computed(() => props.elementParent?.extra?.background_color && props.elementParent?.extra?.background_color != '')
const isCustomSectionBackgroundColor = computed(() => editorPageStore.getSectionExtra(props.sectionIndex)?.background_color != '')
const backgroundImage = computed(() => props.extra?.background_image?.uri ?? null)
const backgroundImageOpacity = computed(() => props.extra?.background_image_opacity ?? '80')
const backgroundFocalPoint = computed(() => props.extra?.background_image_focal_point ?? { x: 50, y: 50 })

const backgroundColor = computed(() => {
    if (props.sectionHasBackgroundImage || (!!backgroundImage.value && Number(backgroundImageOpacity.value) > 50)) {
        return 'transparent'
    }

    if (isCustomBackgroundColor.value) return props.extra?.background_color
    if (isCustomParentBackgroundColor.value) return props.elementParent.extra?.background_color
    if (isCustomSectionBackgroundColor.value) return editorPageStore.getSectionExtra(props.sectionIndex)?.background_color

    return props.sectionBackgroundColor
})

const textColor = computed(() => {
    if (
        (props.sectionHasBackgroundImage && !isCustomBackgroundColor.value)
        || (backgroundImage.value && Number(backgroundImageOpacity.value) > 50)
    ) {
        return layoutStore.getSectionTextColorInverted()
    }

    if (isCustomBackgroundColor.value || isCustomParentBackgroundColor.value || isCustomSectionBackgroundColor.value) {
        return layoutStore.getAdaptiveTextColor(backgroundColor.value)
    }

    return layoutStore.getSectionTextColorDefault(props.sectionIndex)
})

const dynamicStyle = computed(() => {
    if (!hasExtra.value) return

    return {
        margin: `${props.extra?.margin?.top ?? 0}px ${props.extra?.margin?.right ?? 0}px ${props.extra?.margin?.bottom ?? 0}px ${props.extra?.margin?.left ?? 0}px`,
        padding: `${props.extra?.padding?.top ?? 0}px ${props.extra?.padding?.right ?? 0}px ${props.extra?.padding?.bottom ?? 0}px ${props.extra?.padding?.left ?? 0}px`,
        color: textColor.value,
        backgroundColor: backgroundColor.value,
    }
})

const isEmpty = computed(() => {
    return !content.value || content.value == null || content.value.replace(/<[^>]*>/g, '').trim() == ''
})

const syncTextContainerHeight = async () => {
    await nextTick()

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    }

    const elementRoot = editorText.value
    const contentRoot = elementRoot?.querySelector?.('.tiptap') as HTMLElement | null

    if (!elementRoot || !contentRoot) return

    elementRoot.style.minHeight = '0px'
    elementRoot.style.minHeight = `${contentRoot.scrollHeight}px`
}

const onFocusEditorText = () => {
    betaStore.setSelectedElementBySchema(props.schema, props.element)
    betaStore.setEditingElement(props.element)
    betaStore.setActiveTextEditor(editor.value)
    editor.value?.commands.focus()
    syncActiveTextSelectionState(editor.value)
}

const onBlurEditorText = (event?: any) => {
    const target = event?.target as HTMLElement | null
    const targetClassName = typeof target?.className === 'string' ? target.className : ''
    const clickedEditorElement = target?.closest?.('[data-beta-element-root], [schema]')
    const shouldPreserveTextSelection = betaStore.shouldKeepActiveTextSelection()
        && !clickedEditorElement

    const shouldKeepTextContext = Boolean(
        target?.closest?.('.beta-editor-sidebar')
        || target?.closest?.('.beta-element-actions')
        || target?.closest?.('.editor-text-empty-actions')
        || target?.closest?.('.modal')
        || target?.closest?.('[role="dialog"]')
        || targetClassName.includes('vc-')
        || targetClassName.includes('color-')
        || target?.closest?.('.tippy-box')
        || target?.closest?.('[data-keep-beta-text-focus]')
        || target?.closest?.('.text-link-modal')
        || target?.closest?.('.text-ai-modal')
        || target?.closest?.('input')
        || target?.closest?.('textarea')
        || target?.closest?.('select')
        || shouldPreserveTextSelection
    )

    if (shouldKeepTextContext) return

    if (!clickedEditorElement) {
        betaStore.clearActiveTextEditor()
    }

    betaStore.clearEditingElement()

    if (isEmpty.value && !props.element.new) {
        betaStore.removeElementBySchema(props.schema)
    }
}

const insertElementAtCurrentPosition = (newElement: any, position = 0) => {
    betaStore.addElementBySchema(props.schema, newElement, position)
}

const buildGalleryItemFromMedia = (media: any) => ({
    ...defaultStore.newElementGalleryItem,
    type: media.type,
    image: {
        alt: media.name,
        height: media.height,
        url: media.content,
        width: media.width,
        createdAt: media.dataCreate ?? media.datetime ?? new Date().toLocaleString('pt-BR'),
    },
    embed: media.embed ?? null,
})

const handleTransformInGallery = (medias: any[]) => {
    insertElementAtCurrentPosition({
        ...defaultStore.newElementGallery,
        temporaryId: rand(0, 9999999999999),
        frameId: null,
        frameDataId: null,
        frameContent: {
            ...defaultStore.newElementGallery.frameContent,
            items: medias.map(buildGalleryItemFromMedia),
        },
        new: true,
    })
}

const handleTransformInImage = (image: any) => {
    insertElementAtCurrentPosition({
        ...defaultStore.newElementImage,
        temporaryId: rand(0, 9999999999999),
        frameId: null,
        frameDataId: null,
        frameContent: image.content,
        new: true,
    })
}

const handleTransformInVideo = (video: any) => {
    insertElementAtCurrentPosition({
        ...defaultStore.newElementVideo,
        temporaryId: rand(0, 9999999999999),
        frameId: null,
        frameDataId: null,
        frameContent: {
            embed: video.embed,
            thumbnail: video.content,
        },
        new: true,
    })
}

const handleTransformInMedia = (medias: any[]) => {
    if (!Array.isArray(medias) || medias.length === 0) return

    if (medias.length >= 3) {
        handleTransformInGallery(medias)
        return
    }

    medias.forEach((media, index) => {
        if (['image', 'script'].includes(media.type)) {
            handleTransformInImage(media)
            return
        }

        if (['video', 'youtube', 'vimeo'].includes(media.type)) {
            handleTransformInVideo(media)
            return
        }

        if (index === 0) console.error('Erro handleTransformInMedia: type not found!', media)
    })
}

const handleTransformInPreset = (preset: any) => {
    if (!Array.isArray(preset?.frames)) return

    preset.frames
        .map(betaStore.transformPresetFrameToElement)
        .forEach((newElement: any, index: number) => insertElementAtCurrentPosition(newElement, index))
}

const onTransformInMedia = () => {
    mediaManagerStore.setMaxSelectedMedias(100)
    mediaManagerStore.setIsOpenModal(true)
    mediaManagerStore.setAction((medias: any[]) => handleTransformInMedia(medias))
}

const onTransformInTextAI = () => {
    aiStore.setModel('text')
    aiStore.setIsOpenModal(true)
    aiStore.setAction((prompt: string) => {
        const newElement = {
            ...defaultStore.newElementText,
            temporaryId: rand(0, 9999999999999),
            frameId: null,
            frameDataId: null,
            extra: {
                margin: { ...defaultStore.margin },
                padding: { ...defaultStore.padding },
            },
            new: true,
        }

        insertElementAtCurrentPosition(newElement)
        aiStore.generateText(prompt, newElement, props.schema)
        aiStore.reset()
    })
}

const onTransformInPreset = () => {
    presetsStore.setIsOpenModal(true)
    presetsStore.setAction((preset: any) => handleTransformInPreset(preset))
}

watch(content, (value) => {
    if (editor.value?.getHTML() === value) return

    editor.value?.commands.setContent(value || '', { emitUpdate: false })
    syncTextContainerHeight()
})

watch(() => betaStore.previewDevice, () => {
    syncTextContainerHeight()
})

onClickOutside(editorText, (event: any) => onBlurEditorText(event))

onMounted(() => {
    if ((props.element.new || props.element.newBefore) && !props.isChild) {
        onFocusEditorText()
    }

    syncTextContainerHeight()
})

onBeforeUnmount(() => {
    if (betaStore.activeTextEditor === editor.value) {
        betaStore.clearActiveTextEditor()
    }

    editor.value?.destroy()
})
</script>

<template>
    <div
        ref="editorText"
        tabindex="0"
        :class="clsx('editor-text', isEmpty && 'is-empty')"
        :schema="JSON.stringify(schema)"
        :data-active="isActive"
        :data-editing="isEditing"
        :style="dynamicStyle"
        @click="onFocusEditorText"
    >
        <beta-base-image
            v-if="hasExtra && extra.background_image?.uri"
            :src="extra.background_image?.uri"
            class-name="absolute inset-0 h-full w-full object-cover"
            :style="{
                opacity: `${backgroundImageOpacity / 100}`,
                objectPosition: `${backgroundFocalPoint.x}% ${backgroundFocalPoint.y}%`,
            }"
        />

        <editor-content :editor="editor" class="relative z-10 w-full" />

        <div
            v-if="isActive && isEmpty && !props.isChild"
            class="editor-text-empty-actions"
        >
            <button type="button" @click.stop="onTransformInMedia">
                <span class="sr-only">Mídia</span>
                <svg class="h-4 w-4" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M6.05575 9.73537L5.47395 9.0487L5.47394 9.04871L6.05575 9.73537ZM6.09975 9.69322L5.45852 9.0617L5.45781 9.06242L6.09975 9.69322ZM4.24833 11.2668L3.66652 10.5801L3.66651 10.5801L4.24833 11.2668ZM4.14932 11.371L4.88804 11.8851L4.88805 11.8851L4.14932 11.371ZM13.7438 9.79935L13.1205 10.4486L13.7438 9.79935ZM12.2012 8.31847L11.5779 8.96771L11.5779 8.96772L12.2012 8.31847ZM11.6283 8.31444L11.0142 7.65648L11.012 7.65862L11.6283 8.31444ZM8.77106 10.9999L9.38515 11.6578L9.38743 11.6557L8.77106 10.9999ZM8.18939 10.9872L7.54502 11.6155L7.54631 11.6168L8.18939 10.9872ZM8.18189 10.9795L7.53691 11.6071L7.53752 11.6078L8.18189 10.9795ZM8.14106 10.9398L8.73031 10.2595L8.7303 10.2595L8.14106 10.9398ZM6.74912 9.73417L7.33837 9.05388L7.33836 9.05388L6.74912 9.73417ZM6.70813 9.69435L7.35333 9.06688L7.35272 9.06626L6.70813 9.69435ZM6.70079 9.68682L7.34538 9.05873L7.34417 9.05748L6.70079 9.68682ZM6.10766 9.6852L5.4677 9.05238L5.46643 9.05367L6.10766 9.6852ZM12.5996 4.49983V3.59983C12.1026 3.59983 11.6996 4.00278 11.6996 4.49983H12.5996ZM12.8856 4.49983H13.7856C13.7856 4.00278 13.3827 3.59983 12.8856 3.59983V4.49983ZM12.8856 4.74202V5.64202C13.3827 5.64202 13.7856 5.23908 13.7856 4.74202H12.8856ZM12.5996 4.74202H11.6996C11.6996 5.23908 12.1026 5.64202 12.5996 5.64202V4.74202ZM0.1 4V14H1.9V4H0.1ZM4 17.9H14V16.1H4V17.9ZM17.9 14V4H16.1V14H17.9ZM14 0.1H4V1.9H14V0.1ZM17.9 4C17.9 1.84609 16.1539 0.1 14 0.1V1.9C15.1598 1.9 16.1 2.8402 16.1 4H17.9ZM14 17.9C16.1539 17.9 17.9 16.1539 17.9 14H16.1C16.1 15.1598 15.1598 16.1 14 16.1V17.9ZM0.1 14C0.1 16.1539 1.84609 17.9 4 17.9V16.1C2.8402 16.1 1.9 15.1598 1.9 14H0.1ZM1.9 4C1.9 2.8402 2.8402 1.9 4 1.9V0.1C1.84609 0.1 0.1 1.84609 0.1 4H1.9ZM6.63754 10.422C6.68962 10.3779 6.7323 10.3336 6.74169 10.324L5.45781 9.06242C5.45396 9.06635 5.45219 9.06814 5.45071 9.06963C5.44943 9.07092 5.44965 9.07067 5.45081 9.06955C5.45291 9.06751 5.46122 9.05949 5.47395 9.0487L6.63754 10.422ZM4.83013 11.9535L6.63755 10.422L5.47394 9.04871L3.66652 10.5801L4.83013 11.9535ZM4.88805 11.8851C4.86863 11.913 4.8513 11.9322 4.84122 11.9426C4.83143 11.9526 4.82615 11.9568 4.83014 11.9534L3.66651 10.5801C3.60397 10.6331 3.50234 10.7251 3.41059 10.857L4.88805 11.8851ZM4.9 11.847C4.9 11.8607 4.8958 11.874 4.88804 11.8851L3.41059 10.857C3.20907 11.1465 3.1 11.4919 3.1 11.847H4.9ZM4.9 13.5833V11.847H3.1V13.5833H4.9ZM4.41667 13.1C4.6836 13.1 4.9 13.3164 4.9 13.5833H3.1C3.1 14.3105 3.68949 14.9 4.41667 14.9V13.1ZM13.5833 13.1H4.41667V14.9H13.5833V13.1ZM13.1 13.5833C13.1 13.3164 13.3164 13.1 13.5833 13.1V14.9C14.3105 14.9 14.9 14.3105 14.9 13.5833H13.1ZM13.1 10.4005V13.5833H14.9V10.4005H13.1ZM13.1205 10.4486C13.1074 10.436 13.1 10.4187 13.1 10.4005H14.9C14.9 9.92853 14.7075 9.47696 14.3671 9.1501L13.1205 10.4486ZM11.5779 8.96772L13.1205 10.4486L14.3671 9.1501L12.8245 7.66922L11.5779 8.96772ZM12.2424 8.97239C12.0548 9.1475 11.763 9.14544 11.5779 8.96771L12.8245 7.66922C12.3201 7.18506 11.5253 7.17947 11.0142 7.65649L12.2424 8.97239ZM9.38743 11.6557L12.2447 8.97025L11.012 7.65862L8.15469 10.344L9.38743 11.6557ZM7.54631 11.6168C8.04189 12.1229 8.85827 12.1496 9.38515 11.6578L8.15697 10.3419C8.35062 10.1612 8.65007 10.1712 8.83247 10.3575L7.54631 11.6168ZM7.53752 11.6078L7.54502 11.6155L8.83376 10.3588L8.82625 10.3511L7.53752 11.6078ZM7.55181 11.6201C7.54002 11.6099 7.53232 11.6023 7.53038 11.6004C7.52932 11.5994 7.52911 11.5992 7.5303 11.6004C7.53168 11.6018 7.53332 11.6035 7.53691 11.6071L8.82687 10.3518C8.81816 10.3428 8.77847 10.3012 8.73031 10.2595L7.55181 11.6201ZM6.15988 10.4145L7.55182 11.6201L8.7303 10.2595L7.33837 9.05388L6.15988 10.4145ZM6.06293 10.3218C6.0717 10.3308 6.11152 10.3726 6.15988 10.4145L7.33836 9.05388C7.35018 9.06412 7.35791 9.07168 7.35987 9.07361C7.36094 9.07467 7.36115 9.07489 7.35995 9.07368C7.35858 9.07228 7.35693 9.07058 7.35333 9.06688L6.06293 10.3218ZM6.0562 10.3149L6.06354 10.3224L7.35272 9.06626L7.34538 9.05873L6.0562 10.3149ZM6.74762 10.318C6.5571 10.5107 6.24714 10.5101 6.05742 10.3162L7.34417 9.05748C6.83009 8.53195 5.98396 8.5303 5.4677 9.05238L6.74762 10.318ZM6.74099 10.3247L6.74889 10.3167L5.46643 9.05367L5.45852 9.0617L6.74099 10.3247ZM12.5996 5.39983H12.8856V3.59983H12.5996V5.39983ZM11.9856 4.49983V4.74202H13.7856V4.49983H11.9856ZM12.8856 3.84202H12.5996V5.64202H12.8856V3.84202ZM13.4996 4.74202V4.49983H11.6996V4.74202H13.4996Z"
                    />
                </svg>
            </button>

            <button type="button" @click.stop="onTransformInTextAI">
                <span class="sr-only">Texto IA</span>
                <svg class="h-[21px] w-[22px]" fill="currentColor" viewBox="0 0 22 21" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill="var(--main-color)"
                        d="M14.9448 0L14.6043 0.69608C14.4255 1.06159 14.1356 1.358 13.778 1.54077L13.097 1.88874L13.7779 2.23671C14.1355 2.41946 14.4255 2.7159 14.6043 3.0814L14.9448 3.77748L15.2853 3.0814C15.4641 2.71589 15.7541 2.41948 16.1117 2.23671L16.7925 1.88874L16.1115 1.54077C15.7539 1.35802 15.4641 1.06158 15.2853 0.69608L14.9448 0Z"
                    />
                    <path
                        fill="var(--main-color)"
                        d="M1.84782 13L1.50724 13.6961C1.32844 14.0616 1.03859 14.358 0.681001 14.5408L0 14.8887L0.680842 15.2367C1.03844 15.4195 1.32842 15.7159 1.50724 16.0814L1.84782 16.7775L2.18825 16.0814C2.36704 15.7159 2.65705 15.4195 3.01464 15.2367L3.69548 14.8887L3.01448 14.5408C2.65689 14.358 2.36706 14.0616 2.18824 13.6961L1.84782 13Z"
                    />
                    <path
                        fill="var(--main-color)"
                        d="M19.2283 7L19.8767 5.67438C20.0555 5.30887 20.3455 5.01246 20.7031 4.82968L22 4.16695L20.7031 3.50422C20.3455 3.32147 20.0555 3.02503 19.8767 2.65953L19.2283 1.3339L18.58 2.65953C18.4012 3.02504 18.1111 3.32145 17.7536 3.50406L16.4567 4.16695L17.7536 4.82985C18.1112 5.01244 18.4011 5.30887 18.58 5.67438L19.2283 7Z"
                    />
                    <path
                        d="M15.0616 5H3.07276C2.48954 5 2.01493 5.48945 2.01493 6.09091V9C2.01493 9.60145 2.48954 10.0909 3.07276 10.0909C3.65598 10.0909 4.1306 9.60145 4.1306 9V7.18182H8.00934V18.8182H5.89366C5.31044 18.8182 4.83583 19.3076 4.83583 19.9091C4.83583 20.5105 5.31044 21 5.89366 21H12.2407C12.8239 21 13.2985 20.5105 13.2985 19.9091C13.2985 19.3076 12.8239 18.8182 12.2407 18.8182H10.125V7.18182H14.0037V9C14.0037 9.60145 14.4784 10.0909 15.0616 10.0909C15.6448 10.0909 16.1194 9.60145 16.1194 9V6.09091C16.1194 5.48945 15.6448 5 15.0616 5Z"
                    />
                </svg>
            </button>

            <button type="button" @click.stop="onTransformInPreset">
                <span class="sr-only">Template</span>
                <svg class="h-4 w-4" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M7.4 7C7.4 6.50294 6.99706 6.1 6.5 6.1C6.00294 6.1 5.6 6.50294 5.6 7H7.4ZM5.6 17C5.6 17.4971 6.00294 17.9 6.5 17.9C6.99706 17.9 7.4 17.4971 7.4 17H5.6ZM4 1.9H14V0.1H4V1.9ZM16.1 4V14H17.9V4H16.1ZM14 16.1H4V17.9H14V16.1ZM1.9 14V4H0.1V14H1.9ZM4 16.1C2.8402 16.1 1.9 15.1598 1.9 14H0.1C0.1 16.1539 1.84609 17.9 4 17.9V16.1ZM16.1 14C16.1 15.1598 15.1598 16.1 14 16.1V17.9C16.1539 17.9 17.9 16.1539 17.9 14H16.1ZM14 1.9C15.1598 1.9 16.1 2.8402 16.1 4H17.9C17.9 1.84609 16.1539 0.1 14 0.1V1.9ZM4 0.1C1.84609 0.1 0.1 1.84609 0.1 4H1.9C1.9 2.8402 2.8402 1.9 4 1.9V0.1ZM5.6 7V17H7.4V7H5.6ZM1.5 7.4H16.5V5.6H1.5V7.4Z"
                    />
                </svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
.editor-text {
    @apply relative flex min-w-0 max-w-full border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-text[data-active='true'],
.editor-text[data-editing='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}

.editor-text :deep(.tiptap) {
    @apply w-full outline-none;
}

.editor-text :deep(.tiptap),
.editor-text :deep(.tiptap > *),
.editor-text :deep(.tiptap p),
.editor-text :deep(.tiptap li) {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.editor-text :deep(.tiptap > *) {
    margin-top: 0;
    margin-bottom: 1rem;
}

.editor-text :deep(.tiptap > *:last-child) {
    margin-bottom: 0;
}

.editor-text :deep(.tiptap p) {
    font-size: 1rem;
    line-height: 1.5;
}

.editor-text :deep(.tiptap a) {
    color: #2563eb;
    text-decoration-line: underline;
    background: rgba(37, 99, 235, 0.08);
    border-radius: 3px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    padding: 0 2px;
}

.editor-text :deep(.tiptap h1),
.editor-text :deep(.tiptap h2),
.editor-text :deep(.tiptap h3) {
    font-weight: 700;
    line-height: 1.2;
}

.editor-text :deep(.tiptap h1) {
    font-size: 2.5rem;
}

.editor-text :deep(.tiptap h2) {
    font-size: 2rem;
}

.editor-text :deep(.tiptap h3) {
    font-size: 1.75rem;
}

.editor-text-empty-actions {
    @apply absolute -bottom-5 left-1/2 z-[70] flex -translate-x-1/2 items-center justify-center gap-2;
}

.editor-text-empty-actions button {
    @apply flex h-10 w-10 items-center justify-center rounded-full border border-[#D7D7D7] bg-[rgba(245,245,245,0.96)] text-[#060606] shadow-[0_10px_30px_rgba(15,23,42,0.08)] transition-all duration-200 hover:bg-white;
}
</style>
